// This is the parent component of a modal: ModalAdminmembersone

import styled from "@emotion/styled";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button as AIMEButton } from "tot-loft-component-library";
import GenericModal from "./GenericModal";
import ModalAdminAddError from "./ModalAdminAddError";
import ModalAdminmembersone from "./ModalAdminmembersone";
import Navbar from "./Navbar";
import * as PRTNR from "./prtnr";
import { resetPagination } from "./redux/slices/MembersSlice";
import {
  fetchMembersItems,
  loadMoreMembersItems,
} from "./redux/slices/MembersThunks";

const MembersCounter = ({ totalItems = 0 }) => {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "12px",
        borderRadius: "12px",
        background:
          "linear-gradient(135deg, rgb(33, 37, 67) 0%, rgb(45, 50, 85) 100%)",
        padding: "14px 24px",
        boxShadow:
          "0 2px 8px rgba(0, 0, 0, 0.12), 0 1px 3px rgba(0, 0, 0, 0.08)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        cursor: "default",
        userSelect: "none",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-1px)";
        e.currentTarget.style.boxShadow =
          "0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow =
          "0 2px 8px rgba(0, 0, 0, 0.12), 0 1px 3px rgba(0, 0, 0, 0.08)";
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <span
          style={{
            color: "rgba(255, 255, 255, 0.7)",
            fontSize: "14px",
            fontFamily:
              "Montserrat, -apple-system, BlinkMacSystemFont, sans-serif",
            fontWeight: "500",
            lineHeight: "1.2",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          Total Members:
        </span>
        <span
          style={{
            color: "rgb(255, 255, 255)",
            fontSize: "14px",
            fontFamily:
              "Montserrat, -apple-system, BlinkMacSystemFont, sans-serif",
            fontWeight: "700",
            lineHeight: "1.2",
            letterSpacing: "-0.02em",
          }}
        >
          {totalItems.toLocaleString()}
        </span>
      </div>
    </div>
  );
};
export default function Adminmembersone() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [tableColumn, setTableColumn] = useState([]);
  const [tableHeader, setTableHeader] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");

  // Get data from Redux store
  const {
    items: members,
    loading,
    loadingMore,
    hasMore,
    currentPage,
    error,
    totalItems,
  } = useSelector((state) => state.members);

  // Refs for scroll detection and search debouncing
  const loadingRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const openModal = () => setIsModalOpen(true);
  const openErrorModal = () => setIsErrorModalOpen(true);
  const closeErrorModal = () => setIsErrorModalOpen(false);
  const closeModal = async () => {
    // Refresh data after adding new member
    dispatch(resetPagination());
    dispatch(
      fetchMembersItems({ page: 1, search: searchDebounced, reset: true })
    );
    setIsModalOpen(false);
  };

  const viewClick = (item) => {
    navigate(`/adminmembersone/${item._id}`);
  };

  // Debounced search effect
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearchDebounced(search);
    }, 500); // 500ms delay

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [search]);

  // Load more data function
  const loadMoreData = useCallback(() => {
    if (loadingMore || !hasMore || loading) return;

    dispatch(
      loadMoreMembersItems({
        page: currentPage + 1,
        search: searchDebounced,
      })
    );
  }, [dispatch, currentPage, searchDebounced, loadingMore, hasMore, loading]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          loadMoreData();
        }
      },
      {
        threshold: 0.1,
        rootMargin: "50px",
      }
    );

    if (loadingRef.current) {
      observer.observe(loadingRef.current);
    }

    return () => {
      if (loadingRef.current) {
        observer.unobserve(loadingRef.current);
      }
    };
  }, [loadMoreData, hasMore, loadingMore, loading]);

  // Handle search change - fetch new data
  useEffect(() => {
    dispatch(resetPagination());
    dispatch(
      fetchMembersItems({
        page: 1,
        search: searchDebounced,
        reset: true,
      })
    );
  }, [searchDebounced, dispatch]);

  // Transform data for table display
  const displayData = members.map((val) => ({
    _id: val._id,
    email: val.email_address || val.email || "",
    company_name: val.company_name || "",
    type: val.member_type || val.type || "",
    profile_status: val.profile_status || "",
  }));

  // Handle export functionality
  const handleExport = () => {
    if (!members || members.length === 0) {
      console.log("No data to export");
      return;
    }

    const headers = [
      "Member Type",
      "Email",
      "First Name",
      "Last Name",
      "Phone Number",
      "Job Title",
      "Seniority",
      "Company Name",
      "Website",
      "Country",
      "City",
      "Company Address",
      "Company Registration Number",
      "Tax ID",
    ];

    const csvRows = [];
    csvRows.push(headers.join(","));

    members.forEach((val) => {
      // Helper function to escape CSV values
      const escapeCSV = (value) => {
        if (value === null || value === undefined) return "";
        const stringValue = String(value);
        // Escape quotes and wrap in quotes if contains comma, quote, or newline
        if (
          stringValue.includes(",") ||
          stringValue.includes('"') ||
          stringValue.includes("\n")
        ) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      };

      const formatPhone = (value) => {
        if (!value) return "";
        const stringValue = String(value);
        return `"=""${stringValue}"""`; // forces Excel to treat as text
      };
      const row = [
        escapeCSV(val.member_type),
        escapeCSV(val.email_address || val.email || val.account_id?.email),
        escapeCSV(val.first_name),
        escapeCSV(val.last_name),
        formatPhone(val.phone_number), 
        escapeCSV(val.job_title),
        escapeCSV(val.seniority),
        escapeCSV(val.company_name),
        escapeCSV(val.company_website_url),
        escapeCSV(val.country_of_residence),
        escapeCSV(val.city),
        escapeCSV(val.company_address),
        escapeCSV(val.company_no),
        escapeCSV(val.tax_identification_no),
      ];

      csvRows.push(row.join(","));
    });

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);

    const timestamp = new Date().toISOString().split("T")[0];
    const a = document.createElement("a");
    a.setAttribute("hidden", "");
    a.setAttribute("href", url);
    a.setAttribute("download", `members_${timestamp}.csv`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };
  // Initialize table structure
  useEffect(() => {
    const tableColumns = [
      { key: "email", width: "40%" },
      { key: "company_name", width: "17%" },
      { key: "type", width: "14%" },
      { key: "profile_status", width: "14%" },
      { key: "action", width: "14%" },
    ];

    const tableHeaders = [
      { label: "Email", width: "40%" },
      { label: "Company Name", width: "17%" },
      { label: "Type", width: "14%" },
      { label: "Profile Status", width: "14%" },
      { label: "Action", width: "14%" },
    ];

    setTableColumn(tableColumns);
    setTableHeader(tableHeaders);
  }, []);

  // Initial data load
  useEffect(() => {
    dispatch(
      fetchMembersItems({ page: 1, search: "", limit: 20, reset: true })
    );
  }, [dispatch]);

  return (
    <RootWrapperAdminmembersone>
      <Navbar />
      <Dashboard>
        <Wrapper>
          <Left_0001>
            <PRTNR.SidebarNav />
          </Left_0001>
          <Right>
            <DashboardHeader>
              <Frame1462>
                <WelcomeJames>Members</WelcomeJames>
              </Frame1462>
              <MembersCounter totalItems={totalItems} />

              <Frame1461>
                <AIMEButton
                  size="small"
                  onClick={handleExport}
                  disabled={loading || displayData.length === 0}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    flexDirection: "row",
                    alignItems: "center",
                    flex: "none",
                    gap: "10px",
                    borderRadius: "48px",
                    background:
                      loading || displayData.length === 0
                        ? "#94a3b8"
                        : "rgb(33, 37, 67)",
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "contain",
                    backgroundPosition: "center",
                    boxSizing: "border-box",
                    padding: "16px 32px",
                    cursor:
                      loading || displayData.length === 0
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  <span
                    style={{
                      color: "white",
                      textOverflow: "ellipsis",
                      fontSize: "16px",
                      fontFamily: "Montserrat, sans-serif",
                      fontWeight: "600",
                      lineHeight: "110%",
                      textAlign: "left",
                    }}
                  >
                    Export
                  </span>
                </AIMEButton>
              </Frame1461>
              <Frame1461>
                <AIMEButton
                  size="small"
                  onClick={openModal}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    flexDirection: "row",
                    alignItems: "center",
                    flex: "none",
                    gap: "10px",
                    borderRadius: "48px",
                    background: "rgb(33, 37, 67)",
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "contain",
                    backgroundPosition: "center",
                    boxSizing: "border-box",
                    padding: "16px 32px",
                  }}
                >
                  <span
                    style={{
                      color: "white",
                      textOverflow: "ellipsis",
                      fontSize: "16px",
                      fontFamily: "Montserrat, sans-serif",
                      fontWeight: "600",
                      lineHeight: "110%",
                      textAlign: "left",
                    }}
                  >
                    Add Member
                  </span>
                </AIMEButton>
              </Frame1461>
            </DashboardHeader>

            <ContentBox>
              <TableTitle>
                <Frame1431>
                  <Title>All</Title>
                </Frame1431>
                <Frame1432>
                  <GFieldsStacked>
                    <GFieldsStacked_0001>
                      <PRTNR.Input
                        type="text"
                        placeholder="Search members..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        disabled={loading}
                      />
                    </GFieldsStacked_0001>
                  </GFieldsStacked>
                </Frame1432>
              </TableTitle>

              {/* Error message */}
              {error && (
                <div
                  style={{
                    padding: "16px",
                    marginBottom: "16px",
                    backgroundColor: "#fee2e2",
                    border: "1px solid #fecaca",
                    borderRadius: "8px",
                    color: "#dc2626",
                  }}
                >
                  Error: {error}
                </div>
              )}

              {/* Initial loading state */}
              {loading && displayData.length === 0 ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "40px",
                    color: "#64748B",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <div
                      style={{
                        width: "24px",
                        height: "24px",
                        border: "2px solid #e2e8f0",
                        borderTop: "2px solid #64748B",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                      }}
                    ></div>
                    Loading members...
                  </div>
                </div>
              ) : (
                <>
                  <PRTNR.TableComponent
                    data={displayData}
                    tableColumn={tableColumn}
                    tableHeader={tableHeader}
                    viewClick={viewClick}
                  />

                  {/* Load more indicator */}
                  {loadingMore && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        padding: "20px",
                        color: "#64748B",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <div
                          style={{
                            width: "20px",
                            height: "20px",
                            border: "2px solid #e2e8f0",
                            borderTop: "2px solid #64748B",
                            borderRadius: "50%",
                            animation: "spin 1s linear infinite",
                          }}
                        ></div>
                        Loading more members...
                      </div>
                    </div>
                  )}

                  {/* Intersection observer target */}
                  {hasMore && !loading && !loadingMore && (
                    <div ref={loadingRef} style={{ height: "10px" }}></div>
                  )}

                  {/* End of data indicator */}
                  {!hasMore && displayData.length > 0 && (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "20px",
                        color: "#64748B",
                        fontSize: "14px",
                      }}
                    >
                      You've reached the end of the list
                    </div>
                  )}

                  {/* No data message */}
                  {!loading && displayData.length === 0 && !error && (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "40px",
                        color: "#64748B",
                        fontSize: "16px",
                      }}
                    >
                      {search
                        ? "No members found matching your search."
                        : "No members available."}
                    </div>
                  )}
                </>
              )}
            </ContentBox>
          </Right>
        </Wrapper>
      </Dashboard>

      <Footer>
        <Section>
          <Section_0001>
            <Wrapper_0001>
              <Frame38>
                <_2025PaartnerLimitedAllRightsReserved>
                  © 2025 Paartner Limited. All Rights Reserved
                </_2025PaartnerLimitedAllRightsReserved>
              </Frame38>
            </Wrapper_0001>
          </Section_0001>
        </Section>
      </Footer>

      <GenericModal
        isOpen={isModalOpen}
        onClose={closeModal}
        bg="rgb(226, 232, 240)"
      >
        <ModalAdminmembersone
          isOpen={isModalOpen}
          onClose={closeModal}
          errorModal={openErrorModal}
        />
      </GenericModal>
      <GenericModal
        isOpen={isErrorModalOpen}
        onClose={closeErrorModal}
        bg="rgb(226, 232, 240)"
      >
        <ModalAdminAddError
          isOpen={isErrorModalOpen}
          onClose={closeErrorModal}
        />
      </GenericModal>

      {/* Add CSS for loading spinner animation */}
      <style jsx>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </RootWrapperAdminmembersone>
  );
}
const RootWrapperAdminmembersone = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
  background: rgb(255, 255, 255);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
`;

const Dashboard = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  background: rgb(241, 245, 249);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 0px 60px;
  flex-shrink: 0;
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: flex-start;
  flex: none;
  gap: 24px;
  width: 1200px;
  box-sizing: border-box;
  padding: 32px 0px;
`;

const Left_0001 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: flex-start;
  flex: none;
  gap: 8px;
  width: 298px;
  box-sizing: border-box;
`;

const Right = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  gap: 24px;
  width: 878px;
  box-sizing: border-box;
`;

const DashboardHeader = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Frame1462 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  gap: 4px;
  width: 697px;
  box-sizing: border-box;
`;

const WelcomeJames = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 30px;
  font-family: Montserrat, sans-serif;
  font-weight: 700;
  text-align: center;
`;

const Frame1461 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: flex-start;
  flex: none;
  gap: 8px;
  box-sizing: border-box;
`;

const ContentBox = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  box-shadow: 0px 1px 2px rgba(10, 13, 20, 0.03);
  border: solid 1px rgb(225, 228, 234);
  border-radius: 16px;
  align-self: stretch;
  background: rgb(255, 255, 255);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 24px;
  flex-shrink: 0;
`;

const TableTitle = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: center;
  gap: 20px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Frame1431 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: center;
  flex: none;
  gap: 8px;
  box-sizing: border-box;
`;

const Title = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 20px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
`;

const Frame1432 = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-direction: row;
  align-items: center;
  flex: 1;
  gap: 8px;
  width: 782px;
  box-sizing: border-box;
`;

const GFieldsStacked = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: none;
  box-sizing: border-box;
`;

const GFieldsStacked_0001 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: flex-start;
  flex: none;
  gap: 4px;
  box-sizing: border-box;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Section = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;
  background: rgb(33, 37, 67);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 24px;
  flex-shrink: 0;
`;

const Section_0001 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  box-sizing: border-box;
  padding: 0px 60px;
  flex-shrink: 0;
`;

const Wrapper_0001 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Frame38 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const _2025PaartnerLimitedAllRightsReserved = styled.span`
  color: white;
  text-overflow: ellipsis;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  align-self: stretch;
  flex-shrink: 0;
`;
